import { Component, Inject, HostListener } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { PdfViewerModule } from 'ng2-pdf-viewer';

export interface AttachmentPreviewData {
  documentName: string;
  documentType: string;
  complaintDocument: string; // base64
}

@Component({
  selector: 'app-attachment-preview-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, CommonModule, PdfViewerModule],
  templateUrl: './attachment-preview-dialog.component.html',
  styleUrl: './attachment-preview-dialog.component.css',
})
export class AttachmentPreviewDialogComponent {
  isImage: boolean;
  pdfSrc: Uint8Array | undefined = undefined;
  imageSrc: string | null = null;

  // PDF zoom
  pdfZoom: number = 1;

  // Image zoom
  imageScale: number = 1;
  readonly MIN_SCALE = 0.5;
  readonly MAX_SCALE = 4;
  readonly ZOOM_STEP = 0.25;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AttachmentPreviewData,
    public dialogRef: MatDialogRef<AttachmentPreviewDialogComponent>
  ) {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
    const nameExt = (data.documentName || '').toLowerCase().slice(data.documentName?.lastIndexOf('.'));
    this.isImage = data.documentType?.startsWith('image/') || imageExtensions.includes(nameExt);

    const byteCharacters = atob(data.complaintDocument);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }

    if (this.isImage) {
      this.imageSrc = `data:${data.documentType};base64,${data.complaintDocument}`;
    } else {
      this.pdfSrc = byteArray;
    }
  }

  // Image zoom via mouse wheel
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (!this.isImage) return;
    event.preventDefault();
    const delta = event.deltaY > 0 ? -this.ZOOM_STEP : this.ZOOM_STEP;
    this.setImageScale(this.imageScale + delta);
  }

  zoomIn(): void {
    if (this.isImage) {
      this.setImageScale(this.imageScale + this.ZOOM_STEP);
    } else {
      this.pdfZoom = Math.min(this.pdfZoom + 0.25, 3);
    }
  }

  zoomOut(): void {
    if (this.isImage) {
      this.setImageScale(this.imageScale - this.ZOOM_STEP);
    } else {
      this.pdfZoom = Math.max(this.pdfZoom - 0.25, 0.5);
    }
  }

  resetZoom(): void {
    this.imageScale = 1;
    this.pdfZoom = 1;
  }

  private setImageScale(value: number): void {
    this.imageScale = Math.min(Math.max(value, this.MIN_SCALE), this.MAX_SCALE);
  }

  close(): void {
    this.dialogRef.close();
  }
}
